import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { Reviewer } from "./ReviewersEditList";
import { useMemo } from "react";
import { prettyPrintDate } from "util/dates";
import { PriorityOrderSwitch } from "./PriorityOrderSwitch";
import RemoveFilled from "@mui/icons-material/Remove";
import PersonAdd from "@mui/icons-material/PersonAdd";
import { Box, Alert, Typography } from "@mui/material";
import { DataGridWithAddButton } from "./DataGridWithAddButton";
import { SubmissionFormDatagrid } from "./SubmissionFormDatagrid";
import { DateTime } from "luxon";
import { ReviewMode, Permission } from "generated-graphql/graphql";
import { UseFormSetValue } from "react-hook-form";
import { SubmissionFormData } from "hooks/useSubmissionForm";
import {
  useReviewerIssues,
  useReviewersIssues,
} from "contexts/SubmissionFormContext";
import { IssueCount } from "components/IssueCount";
import { useAuthorization } from "hooks/useAuthorization";
import { useTenant } from "hooks/useTenant";
import { renderIssueMessage } from "./helpers/renderIssueMessage";

type ReviewersDatagridProps = {
  reviewers: Reviewer[];
  setReviewers?: (reviewers: Reviewer[]) => void;
  reviewMode: ReviewMode;
  setValue?: UseFormSetValue<SubmissionFormData>;
  handleAddReviewer: () => void;
  submissionDueDate?: DateTime;
  disabled?: boolean;
};

function setReviewerOrder(reviewers: Reviewer[]) {
  const hasStaff = reviewers.some((r) => r.isStaff);
  reviewers.forEach((r, index) => {
    if (r.isStaff) {
      return;
    }

    r.order = hasStaff ? index : index + 1;
  });
}

export function ReviewersDatagrid({
  reviewers,
  setReviewers,
  reviewMode,
  setValue,
  handleAddReviewer,
  disabled = false,
}: ReviewersDatagridProps) {
  const reviewerIssues = useReviewerIssues();
  const reviewersIssues = useReviewersIssues();
  const { hasPermissions } = useAuthorization();
  const { tenantId } = useTenant();

  const canManageUsers = hasPermissions([Permission.WriteAllUserTenant]);

  // Filter individual reviewer issues by checking if reviewer IDs appear in schemaStack
  const individualReviewerIssues = useMemo(
    () =>
      reviewerIssues.filter((i) =>
        reviewers.some((r) =>
          i.schemaStack?.some((schemaStack) => schemaStack.name === r.id)
        )
      ),
    [reviewerIssues, reviewers]
  );

  // Combine collection-level issues with individual reviewer issues for summary display
  const allReviewerIssues = useMemo(
    () => [...reviewersIssues, ...individualReviewerIssues],
    [reviewersIssues, individualReviewerIssues]
  );

  const augmentedReviewersRows = useMemo(
    () =>
      reviewers.map((r) => ({
        ...r,
        __reorder__: r.name, // this is whats displayed in the little box when you drag and drop
      })),
    [reviewers]
  );
  const columns = useMemo(() => {
    let columns: GridColDef<Reviewer>[] = [];
    if (reviewMode === ReviewMode.Sequential) {
      columns.push({
        field: "order",
        flex: 0.4,
        headerName: "Order",
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) => (row.order !== 0 ? row.order : ""),
      });
    }

    const issuesColumn: GridColDef<Reviewer> = {
      field: "issues",
      display: "flex",
      flex: 0.6,
      headerName: "Issues",
      renderCell: ({ row }) => {
        const issues = reviewerIssues.filter((i) => {
          return i.schemaStack?.some((s) => s.name === row.id);
        });
        return <IssueCount issueCount={issues.length} />;
      },
    };

    columns = columns.concat([
      {
        field: "name",
        display: "flex",
        flex: 1,
        sortable: false,
        headerName: "Reviewer Name",
      },
      {
        field: "email",
        display: "flex",
        flex: 1,
        sortable: false,
        headerName: "Email",
      },
      {
        field: "dueDate",
        display: "flex",
        flex: 1,
        headerName: "Due Date",
        sortable: false,
        valueGetter: (_, row) =>
          row.dueDate ? prettyPrintDate(row.dueDate.toISO()) : "",
      },
    ]);

    if (reviewerIssues.length > 0) {
      columns.push(issuesColumn);
    }

    if (disabled) {
      return columns;
    }

    return columns.concat([
      {
        field: "actions",
        display: "flex",
        flex: 0.25,
        headerName: "",
        renderCell: ({ row }) => {
          return (
            <GridActionsCellItem
              label="Delete"
              icon={<RemoveFilled />}
              onClick={(event) => {
                event.stopPropagation();
                setReviewers?.(reviewers.filter((r) => r.id !== row.id));
                setReviewerOrder(reviewers.filter((r) => r.id !== row.id));
              }}
            />
          );
        },
      },
    ]);
  }, [reviewMode, reviewers, setReviewers, reviewerIssues, disabled]);

  const pinnedRows = useMemo(
    () => ({
      top: augmentedReviewersRows.filter((r) => r.isStaff), // Pin staff reviewers at the top
    }),
    [augmentedReviewersRows]
  );

  return (
    <Box sx={{ mt: 1 }}>
      <PriorityOrderSwitch
        value={reviewMode}
        onChange={(newReviewMode: ReviewMode) =>
          setValue?.("reviewMode", newReviewMode, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
        disabled={disabled}
      />
      {allReviewerIssues.length > 0 && (
        <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
          {allReviewerIssues.map((issue, index) => (
            <Typography key={index} variant="body2">
              {renderIssueMessage(issue.message, canManageUsers, tenantId)}
            </Typography>
          ))}
        </Alert>
      )}
      {disabled ? (
        <SubmissionFormDatagrid
          rows={augmentedReviewersRows}
          columns={columns}
          rowReordering={false}
          pinnedRows={pinnedRows}
        />
      ) : (
        <DataGridWithAddButton
          rows={augmentedReviewersRows}
          columns={columns}
          onAdd={handleAddReviewer}
          createEmptyRow={() => ({
            id: "empty",
            name: "",
            email: "",
            dueDate: undefined,
            streetAddress1: undefined,
            streetAddress2: undefined,
            city: undefined,
            state: undefined,
            zip: undefined,
            country: undefined,
            isSigner: false,
            isStaff: false,
            order: 0,
          })}
          rowReordering={reviewMode === ReviewMode.Sequential}
          pinnedRows={pinnedRows}
          onRowOrderChange={(params) => {
            const { oldIndex, targetIndex } = params;
            const hasStaff = reviewers.some((r) => r.isStaff);
            const o = hasStaff ? oldIndex + 1 : oldIndex;
            const n = hasStaff ? targetIndex + 1 : targetIndex;

            if (setReviewers) {
              const newList = [...reviewers];
              const [movedItem] = newList.splice(o, 1);
              newList.splice(n, 0, movedItem);
              setReviewerOrder(newList);
              setReviewers(newList);

              // Transform reviewers from UI format to form format and trigger validation
              const formReviewers = newList.map((r) => ({
                userId: r.id,
                reviewOrder: r.order,
                isRequired: true,
                isSigner: r.isSigner ?? false,
                isStaff: r.isStaff,
                dueDate: r.dueDate,
              }));

              setValue?.("reviewers", formReviewers, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
          }}
          addButtonConfig={{
            buttonText: "Add Reviewer",
            icon: <PersonAdd />,
          }}
          gridProps={{
            onRowClick: handleAddReviewer,
          }}
        />
      )}
    </Box>
  );
}
